import { Box, Button, Flex, Strong, Text } from '@radix-ui/themes';
import { HELP_URLS } from 'classes/helpers/url.helper';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonLink } from 'components/common/link';
import { CommonTabs } from 'components/common/tabs';
import { PitchUploaderBallTable } from 'components/sections/pitch-uploader/ball-table';
import { PitchUploaderCsvTable } from 'components/sections/pitch-uploader/csv-table';
import { PitchUploaderErrorTab } from 'components/sections/pitch-uploader/error-tab';
import { NoCsvCallout } from 'components/sections/pitch-uploader/no-csv-callout';
import { QualityError } from 'enums/csv';
import { PitchUploaderTab, PitchUploadStep } from 'enums/pitch-uploader';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { usePitchUploaderStore } from 'stores/use-pitch-uploader-store';

export const PitchUploaderPreview = () => {
  const tab = usePitchUploaderStore((s) => s.tab);
  const balls = usePitchUploaderStore((s) => s.balls);
  const validationWarnings = usePitchUploaderStore((s) => s.validationWarnings);
  const qualityWarnings = usePitchUploaderStore((s) => s.qualityWarnings);

  const validBalls = useMemo(() => {
    return balls.filter((m) => !m.fatalError);
  }, [balls]);

  const invalidBalls = useMemo(() => {
    return balls.filter((m) => !!m.fatalError);
  }, [balls]);

  const sortedRawData = useMemo(() => {
    return balls
      .sort((a, b) => {
        // valid first, then invalid
        const [aVal, bVal] = [a, b].map((m) => m.fatalError?.length ?? 0);
        return aVal < bVal ? -1 : 1;
      })
      .map((b) => b.rawData);
  }, [balls]);

  const showWarnings = qualityWarnings.length + validationWarnings.length > 0;

  if (balls.length === 0) {
    return <NoCsvCallout />;
  }

  return (
    <ErrorBoundary identifier="PitchUploaderPreview">
      <Flex direction="column" gap={RADIX.FLEX.GAP.LG}>
        <Box>
          {showWarnings && (
            <CommonCallout
              content={
                <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
                  <Box>
                    <Text>
                      <Strong>
                        {t(
                          'pd.warning-low-quality-data-detected-in-uploaded-file'
                        )}
                      </Strong>
                    </Text>
                  </Box>

                  {qualityWarnings.includes(QualityError.BreakDetails) && (
                    <Box>
                      <ReactMarkdown children={t('pu.warning-breaks-msg')} />
                    </Box>
                  )}

                  {qualityWarnings.includes(QualityError.SeamDetails) && (
                    <Box>
                      <ReactMarkdown children={t('pu.warning-seams-msg')} />
                    </Box>
                  )}

                  {qualityWarnings.includes(QualityError.Statcast) && (
                    <Box>
                      <ReactMarkdown children={t('pu.warning-statcast-msg')} />
                    </Box>
                  )}

                  {Array.from(validationWarnings).map((msg) => (
                    <Box key={`validation-warning-${msg}`}>
                      <ReactMarkdown children={msg} />
                    </Box>
                  ))}

                  <Box>
                    <CommonLink
                      url={t('common.intercom-url') + HELP_URLS.PITCH_UPLOADER}
                    >
                      {t('common.more-info')}
                    </CommonLink>
                  </Box>
                </Flex>
              }
            />
          )}

          <CommonTabs
            value={tab}
            onValueChange={(value) =>
              usePitchUploaderStore.setState({ tab: value as PitchUploaderTab })
            }
            tabs={[
              {
                value: PitchUploaderTab.Errors,
                label: 'common.errors',
                invisible: invalidBalls.length === 0,
                content: <PitchUploaderErrorTab />,
              },
              {
                value: PitchUploaderTab.BallData,
                label: 'Ball Data',
                content: (
                  <PitchUploaderBallTable
                    // key={ballTableKey}
                    data={validBalls}
                    simple
                  />
                ),
              },
              {
                value: PitchUploaderTab.CsvData,
                label: 'CSV Data',
                content: (
                  <PitchUploaderCsvTable
                    // key={csvTableKey}
                    data={sortedRawData}
                    simple
                  />
                ),
              },
            ]}
          />
        </Box>

        <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
          <Box>
            <Button
              onClick={() =>
                usePitchUploaderStore.setState({ step: PitchUploadStep.design })
              }
            >
              {t('common.continue')}
            </Button>
          </Box>
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
